/**
 * Persisted auto start / auto stop preferences.
 *
 * Stored locally (like theme and control size) rather than on the profile:
 * the right trigger depends on the aircraft and the device's GPS, so it is a
 * per-device choice. Both features default to off — recording is safety- and
 * battery-relevant, so the pilot opts in.
 */

import {
  AUTO_RECORD_THRESHOLDS,
  type AutoRecordConfig,
  type AutoStartMode,
} from '@/lib/autoRecordDetector';

export const AUTO_RECORD_STORAGE_KEY = 'flight-track-auto-record';

export type AutoRecordPreferences = {
  startMode: AutoStartMode;
  autoStop: boolean;
};

export const DEFAULT_AUTO_RECORD_PREFERENCES: AutoRecordPreferences = {
  startMode: 'off',
  autoStop: false,
};

function isAutoStartMode(value: unknown): value is AutoStartMode {
  return value === 'off' || value === 'taxi' || value === 'takeoff';
}

/** Parse stored JSON, falling back to the defaults for anything unrecognised. */
export function parseAutoRecordPreferences(raw: string | null): AutoRecordPreferences {
  if (!raw) return { ...DEFAULT_AUTO_RECORD_PREFERENCES };

  try {
    const parsed = JSON.parse(raw) as Partial<AutoRecordPreferences> | null;
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_AUTO_RECORD_PREFERENCES };
    return {
      startMode: isAutoStartMode(parsed.startMode)
        ? parsed.startMode
        : DEFAULT_AUTO_RECORD_PREFERENCES.startMode,
      autoStop:
        typeof parsed.autoStop === 'boolean'
          ? parsed.autoStop
          : DEFAULT_AUTO_RECORD_PREFERENCES.autoStop,
    };
  } catch {
    return { ...DEFAULT_AUTO_RECORD_PREFERENCES };
  }
}

export function serializeAutoRecordPreferences(preferences: AutoRecordPreferences): string {
  return JSON.stringify(preferences);
}

/** Combine the pilot's choice with the tuned detection thresholds. */
export function autoRecordConfigFrom(preferences: AutoRecordPreferences): AutoRecordConfig {
  return { ...AUTO_RECORD_THRESHOLDS, ...preferences };
}

export function autoStartModeLabel(mode: AutoStartMode): string {
  if (mode === 'taxi') return 'When taxiing';
  if (mode === 'takeoff') return 'On takeoff';
  return 'Off';
}
